import React, { useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { useNotifier } from '@cbgms/base/notifier'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { generateSubmissionError } from 'utils-support/Form/SubmissionError'
import { createShop, listShops, updateShopLocalizationSettings } from 'app-support/shops/state/actions'
import { listThemes } from 'app-support/themes/state/actions'
import ShopForm, { IFormValues } from './ShopForm'
import { EmptyUUID } from '@cbgms/base/utils/uuid'
import { GETThemesResponse, ISupportShop, POSTThemeResponse } from '@cbgms/api'
import useQuery from '@cbgms/base/http/use-query'
import { query } from '@cbgms/components/list'
import { ShopListRow } from './List'
import { ConfirmDialogPromise } from '@carsys/ui'

const newShopForm = 'shop_new'

const NewShop: React.FC<{}> = () => {
  const { companyUUID } = useParams<{ companyUUID: string }>()
  const { t } = useTranslation()
  const notifier = useNotifier()
  const dispatch = useDispatch()
  // functions passed as props to submit
  const createShopD = useCallback(input => dispatch(createShop(input)), [dispatch])
  const updateShopLocalizationSettingsD = useCallback((input, data) => dispatch(updateShopLocalizationSettings(input, data)), [dispatch])
  const redirectURL = `/companies/edit/${companyUUID}`

  const onSubmitSuccess = () => {
    notifier.success(t('shop.created_successfully'))
  }

  const selectInitialThemeValue = (data: GETThemesResponse | undefined) => {
    if (data && data?.Data?.length > 0) {
      const result = data?.Data.find(theme => theme.Theme.IsDefault)
      return (result && result.Theme.UUID) || data.Data[0].Theme.UUID
    }

    return ''
  }

  const onSubmit = async (values: IFormValues) => {
    const VisitorLongitude = parseFloat(values?.Shop?.VisitorLongitude?.toString())
    const VisitorLatitude = parseFloat(values?.Shop?.VisitorLatitude?.toString())
    const body = {
      ...values?.Shop,
      VisitorLongitude: Number.isNaN(VisitorLongitude) ? undefined : VisitorLongitude,
      VisitorLatitude: Number.isNaN(VisitorLatitude) ? undefined : VisitorLatitude,
      CompanyUUID: companyUUID
    }

    const updateShopLocalizationSettingsBody = {
      ...values.ShopLocalizationSettings,
      DateNotation: undefined // don't want to set DateNotation, it's stored on the backend but not configurable by user
    }

    try {
      const search = query<ShopListRow>()
        .contains(i => i.Shop.PhoneNumber, values.Shop.PhoneNumber)
        .toString()

      const duplicates = await dispatch(
        listShops({
          fields: 'Shop.*',
          search
        })
      )
      if (duplicates.Data.length) {
        await ConfirmDialogPromise({
          content: (
            <div>
              {t('Auth.OnboardingDuplicateOnPhoneNumber')}
              <ul>
                {duplicates.Data.map(shop => (
                  <li key={shop.Shop.ID}>
                    <a href={`/companies/${shop.Shop.CompanyUUID}/shops/edit/${shop.Shop.ID}`}>
                      {shop.Shop.Name} ({shop.Shop.ID})
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ),
          confirmButtonContent: t('global.confirm'),
          cancelButtonContent: t('global.cancel')
        })
      }

      const { Data } = (await createShopD(body)) as any as POSTThemeResponse
      // Data.UUID is actually a shopID
      await updateShopLocalizationSettingsD(Data.UUID, updateShopLocalizationSettingsBody)
      return `/companies/${companyUUID}/shops/edit/${Data.UUID}`
    } catch (error) {
      throw generateSubmissionError(error)
    }
  }

  const onSubmitAndDone = async (values: IFormValues) => {
    try {
      await onSubmit(values)
      return redirectURL
    } catch (error) {
      throw error
    }
  }

  const { data: dataTheme, isLoading: isLoadingTheme } = useQuery([newShopForm, 'get-themes'], async () => {
    const themes: GETThemesResponse = (await dispatch(
      listThemes({
        search: 'Theme.IsActive|=|true',
        fields: 'Theme.UUID,Theme.IsDefault'
      })
    )) as any
    return themes
  })

  const additionalProps = useMemo(
    () => ({
      form: newShopForm,
      onCancel: redirectURL,
      onSubmit,
      onSubmitAndDone,
      onSubmitSuccess,
      initialValues: {
        Shop: {
          AuthorizedForSources: [],
          TenderEngineWorkshopID: EmptyUUID,
          ThemeUUID: selectInitialThemeValue(dataTheme),
          VisitorLatitude: 0,
          VisitorLongitude: 0,
          ModuleAddOrderAccount: true,
          ModuleSwitchDefaultOrderAccount: true
        } as unknown as ISupportShop,
        ExternalGMS: { Name: '' }
      }
    }),
    [newShopForm, dataTheme]
  )
  if (isLoadingTheme) {
    return null
  }

  return (
    <ShopForm
      {...additionalProps}
      form={newShopForm}
      createShop={createShopD}
      updateShopLocalizationSettings={updateShopLocalizationSettingsD}
    />
  )
}

export default NewShop
